import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Shield, Network, Server, Headphones, CheckCircle, ArrowRight } from 'lucide-react';
import config from '../config';
import './Services.css';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const iconMap = {
    'shield': <Shield size={50} />,
    'network': <Network size={50} />,
    'server': <Server size={50} />,
    'headset': <Headphones size={50} />
  };

  const unsplashImages = {
    'shield': 'https://images.unsplash.com/photo-1550745665-5bcfa8209023?w=400&h=400&fit=crop',
    'network': 'https://images.unsplash.com/photo-1558494949-ef010cbffb31?w=400&h=400&fit=crop',
    'server': 'https://images.unsplash.com/photo-1558494949-ef010cbffb31?w=400&h=400&fit=crop',
    'headset': 'https://images.unsplash.com/photo-1580678683292-cd3ec928cde0?w=400&h=400&fit=crop'
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/services.php`);
      setServices(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading services...</p>
      </div>
    );
  }

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">Our Services</div>
            <h1 className="page-title">Enterprise IT Solutions</h1>
            <p className="page-subtitle">
              Comprehensive services designed for modern enterprise operations
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Service Portfolio</h2>
            <p className="section-subtitle">
              End-to-end IT operations and security solutions for global enterprises
            </p>
          </div>
          
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={service.id} className="service-overview-card">
                <div className="service-number">{index + 1}</div>
                <div className="service-icon">
                  {iconMap[service.icon] || <Server size={50} />}
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <Link to={`/services#${service.id}`} className="service-link">
                  Learn more <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="section" style={{ background: '#fafafa' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Service Details</h2>
            <p className="section-subtitle">
              In-depth look at our comprehensive service offerings
            </p>
          </div>
          
          <div className="services-list">
            {services.map((service, index) => (
              <div key={service.id} className={`service-detail-card ${index % 2 === 0 ? 'left-image' : 'right-image'}`} id={service.id}>
                <div className="service-header">
                  <div className="service-number-large">{index + 1}</div>
                  <div className="service-icon-large">
                    {iconMap[service.icon] || <Server size={50} />}
                  </div>
                  <div className="service-title-section">
                    <h2>{service.title}</h2>
                    <div className="service-status">
                      <span className={`status-badge ${service.status}`}>
                        {service.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="service-body">
                  <div className="service-content">
                    <div className="service-description-box">
                      <h3>Overview</h3>
                      <p className="service-description">{service.description}</p>
                    </div>
                    {service.features && service.features.length > 0 && (
                      <div className="service-features">
                        <h3>Key Features & Capabilities</h3>
                        <div className="features-grid">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="feature-item">
                              <CheckCircle size={20} />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="service-actions">
                      <Link to="/contact" className="btn-primary">
                        Get Started
                      </Link>
                      <Link to="/contact" className="btn-secondary">
                        Learn More
                      </Link>
                    </div>
                  </div>
                  <div className="service-visual">
                    <div className="service-image-container">
                      <div className="service-image-placeholder">
                        <img 
                          src={unsplashImages[service.icon] || unsplashImages.server} 
                          alt={service.title}
                          className="service-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="service-icon-fallback">
                          {iconMap[service.icon] || <Server size={80} />}
                        </div>
                      </div>
                      <div className="service-image-bg"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section">
        <div className="container">
          <div className="cta-box">
            <h2>Ready to Transform Your IT Operations?</h2>
            <p>Contact us today to discuss how we can support your business requirements and help you achieve operational excellence.</p>
            <div className="cta-actions">
              <Link to="/contact" className="btn-primary">
                Contact Us Now
              </Link>
              <Link to="/about" className="btn-secondary">
                Learn About Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
